// ABOUTME: Draws one cloud tier from its geometry: a rounded slab plus ellipses, all one flat fill.
// ABOUTME: The box is an anchor, never a clip; the copy layer sits above the parts.
import type { CSSProperties, ReactNode } from 'react';
import { Box } from '@mui/material';
import { useCrossingReducedMotion } from '../../crossing/useReducedMotion';
import type { EllipsePart, SlabPart, Tier } from './stackGeometry';
import { DIMMED_OPACITY, settleSx, settleStyle } from './settleFade';
import { stackBuildStyle, stackBuildSx } from './stackBuild';
import type { StackBuildStep } from './stackBuild';

interface SculptedCloudProps {
  tier: Tier;
  fill: string;
  testId: string;
  // True on the first paint so a reload shows the stored state without replaying a reveal.
  instant?: boolean;
  // Paints the tier above its neighbours (the question tier on 5B).
  z?: number;
  // Steps the tier back and out of the reading order (the other tiers on 5B).
  dimmed?: boolean;
  // The 3A entrance: the tier fades back and its words with it, out of the reading order. With `keepWords` only the
  // silhouette fades and the words stay full, as the base's word and icon do.
  settled?: boolean;
  keepWords?: boolean;
  // The settled fades are already played: the tier sits faded from the first paint (4A draws the entrance finished).
  still?: boolean;
  fadeStyle?: CSSProperties;
  // The forgiven end state (4B): the tier is at full strength and in the reading order, whatever the settle says. The
  // transition is how it gets there ('none' on arrival, the lighten transition on the press).
  lit?: boolean;
  litTransition?: string;
  // The 2B build's entrance for this tier.
  build?: StackBuildStep;
  children?: ReactNode;
}

const FILL_TRANSITION = 'background 1.1s ease';
const OPACITY_TRANSITION = 'opacity 1s ease';

function slabSx(part: SlabPart) {
  return {
    position: 'absolute',
    left: part.left,
    right: part.right,
    bottom: part.bottom,
    height: `${part.heightPct}%`,
    borderRadius: part.radius,
  } as const;
}

function ellipseSx(part: EllipsePart) {
  return {
    position: 'absolute',
    left: `${part.leftPct}%`,
    bottom: `${part.bottomPct}%`,
    width: `${part.widthPct}%`,
    height: `${part.heightPct}%`,
    borderRadius: '50%',
  } as const;
}

export default function SculptedCloud({ tier, fill, testId, instant = false, z, dimmed = false, settled = false, keepWords = false, still = false, fadeStyle, lit = false, litTransition = 'none', build, children }:SculptedCloudProps) {
  const reducedMotion = useCrossingReducedMotion();
  const [x, y, w, h] = tier.box;
  const transition = reducedMotion || instant ? 'none' : FILL_TRANSITION;
  const settling = settled && !lit;
  const settleProps = settling ? { 'aria-hidden': true, 'data-copy-fade': true } : {};
  const fade = settling ? settleSx(reducedMotion || still, !keepWords) : undefined;
  const fadeStyleOf = settling ? settleStyle(reducedMotion || still) : undefined;
  const litSx = lit ? { opacity: 1, transition: litTransition, '& .MuiTypography-root': { transition: litTransition } } : {};

  return (
    <Box
      data-testid={testId}
      {...(dimmed ? { 'aria-hidden': true } : {})}
      {...settleProps}
      style={build ? stackBuildStyle(build) : keepWords ? undefined : fadeStyle ?? fadeStyleOf}
      sx={{
        position: 'absolute', left: x, top: y, width: w, height: h, zIndex: z ?? tier.z, overflow: 'visible', pointerEvents: 'none',
        opacity: dimmed ? DIMMED_OPACITY : 1, transition: reducedMotion || instant ? 'none' : OPACITY_TRANSITION,
        ...(build ? stackBuildSx(build) : {}), ...(keepWords ? {} : fade), ...litSx,
      }}
    >
      <Box
        data-testid={keepWords ? 'feeling-cloud-fill' : undefined}
        style={keepWords ? fadeStyleOf : undefined}
        sx={{ position: 'absolute', inset: 0, ...(keepWords ? fade : {}), ...(keepWords ? litSx : {}) }}
      >
      {tier.parts.map((part, i) => (
        <Box
          key={i}
          data-part={part.kind}
          sx={{
            ...(part.kind === 'slab' ? slabSx(part) : ellipseSx(part)),
            backgroundColor: fill,
            boxShadow: 'none',
            transition,
          }}
        />
      ))}
      </Box>
      <Box
        data-testid={keepWords ? 'feeling-cloud-words' : undefined}
        sx={{
          position: 'absolute', inset: 0, zIndex: 5, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box', textAlign: 'center',
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
